/**
 * Market (region) the SPA is running as: "global" (US and international,
 * USD) or "india" (INR, India plan limits and content).
 *
 * www.vowsera.com serves every country (owner decision 2026-09-15), so the
 * market is the visitor's choice from the header region picker, remembered
 * per browser; a signed-in account's own market takes over (App.jsx). A
 * domain dedicated to a market (vowsera.in, if it is ever pointed here)
 * always wins.
 *
 * The backend resolves the same markets (config/markets.php) from the
 * X-VowsEra-Market header that api.js sends on every request. GET /market
 * returns the live settings; MARKET_DEFAULTS apply until it answers.
 */

const MARKET_DOMAINS = [
  { key: 'india', pattern: /(^|\.)vowsera\.in$/i },
];

export const MARKET_KEYS = ['global', 'india'];
export const MARKET_CHOICE_KEY = 'vowsera-market';
// Dev-only override from the first version of phase 2; removed 2026-09-15.
const LEGACY_OVERRIDE_KEY = 'vowsera-market-override';

/** { key, source } — source is 'domain', 'choice' or 'default'. */
export const detectMarket = (loc = window.location, storage = globalThis.localStorage) => {
  const host = loc?.hostname || '';
  try { storage?.removeItem(LEGACY_OVERRIDE_KEY); } catch { /* storage blocked */ }

  const domain = MARKET_DOMAINS.find(m => m.pattern.test(host));
  if (domain) return { key: domain.key, source: 'domain' };

  try {
    const saved = storage?.getItem(MARKET_CHOICE_KEY);
    if (MARKET_KEYS.includes(saved)) return { key: saved, source: 'choice' };
  } catch { /* storage blocked */ }

  return { key: 'global', source: 'default' };
};

const detected = typeof window !== 'undefined' ? detectMarket() : { key: 'global', source: 'default' };

/** Resolved once when the app loads; switching market reloads the page. */
export const MARKET = detected.key;
export const MARKET_SOURCE = detected.source;

/** Remember a market choice for this browser. Returns false for unknown keys. */
export const chooseMarket = (key, storage = globalThis.localStorage) => {
  if (!MARKET_KEYS.includes(key)) return false;
  try { storage?.setItem(MARKET_CHOICE_KEY, key); } catch { /* storage blocked */ }
  return true;
};

export const MARKET_DEFAULTS = {
  global: {
    key: 'global',
    name: 'VowsEra',
    label: 'United States & international (USD)',
    countries: ['US'],
    defaultCountry: 'US',
    currency: 'USD',
    locale: 'en-US',
    dateFormat: 'MM-DD-YYYY',
    languages: ['en'],
    paymentGateway: 'stripe',
    comingSoon: false,
    planPrices: null, // config/plans.php + Stripe; the pricing pages keep their USD fallbacks
  },
  india: {
    key: 'india',
    name: 'VowsEra India',
    label: 'India (₹ INR)',
    countries: ['IN'],
    defaultCountry: 'IN',
    currency: 'INR',
    locale: 'en-IN',
    dateFormat: 'DD-MM-YYYY',
    languages: ['en', 'hi', 'ta', 'gu'],
    paymentGateway: 'razorpay',
    // Treated as closed until GET /market says otherwise.
    comingSoon: true,
    planPrices: {
      free: { monthly: 0, yearly: 0 },
      silver: { monthly: 499, yearly: 2499 },
      gold: { monthly: 999, yearly: 4999 },
      platinum: { monthly: 1999, yearly: 9999 },
    },
  },
};

export const marketDefaults = (key = MARKET) => MARKET_DEFAULTS[key] || MARKET_DEFAULTS.global;

/** The market a country belongs to ("IN" → india). */
export const countryMarket = (code) =>
  Object.values(MARKET_DEFAULTS).find(m => m.countries.includes(code))?.key || 'global';
